import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { Box, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  setHasAgreed,
  saveDraftContract,
  saveTravelContractInCache,
  clearCalculationBackendErrors,
} from '../../../../redux/modules/travelContract';
import { loadAvailableDestinations } from '../../../../redux/modules/travelDestinations';
import {
  selectCoverages,
  selectDestinations,
  selectLimit,
  selectPackage,
  selectPlan,
  setAge,
  setDays,
  setRange,
  setRegion,
} from '../../../../redux/modules/travelCalculation';
import TravelCalculator from '../../../../components/TravelCalculator';
import TitleTypography from '../../../../components/MUI/TitleTypography';
import StepNavigationAmount from '../../../../components/MUI/StepNavigationAmount';
import StepsNavigation from '../../../../components/StepsNavigation';
import PromoCodes from '../../../../components/PromoCodes';
import { MAIN_BUTTON_WIDTH_KEYS } from '../../../../components/MUI/MainButton';
import InfoBlock from '../../../../components/InfoBlock';
import { contractCalculatorSchema } from '../../../../validations/travelCalcValidations';
import {
  getDiscountedPrice,
  getOnlyLiteDisallowedCountry,
  getPlansWithAllowedLimitRegionAndAge,
} from '../../../../helpers/travel/travelCalcHelpers';
import { getDiffInYearsFromNow, getEarliestDateFromArray } from '../../../../helpers/dates';
import useScrollToFormErrorElement from '../../../../hooks/useScrollToFormErrorElement';
import usePrevious from '../../../../hooks/usePrevious';
import { ACTION_NAME } from '../../../../constants';

import useStyles from './makeStyles';

const travelDataSelector = (state) => state.travelContract.contractData.travelDetails;

const CalculationStep = ({ goPrevious, goNext, blockNextSteps, params }) => {
  const {
    isFetchingPrice,
    totalPrice,
    loader,
    actionName,
    handleOpenMobileSummary,
    isDeviceLgSize,
    setStepNavigationBox,
  } = params;

  const classes = useStyles();
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const [submitCount, setSubmitCount] = useState(0);
  const previousCount = usePrevious(submitCount);

  const isAuthenticated = useSelector((store) => store.auth.isAuthenticated);
  const isSavingCache = useSelector((state) => state.travelContract.savingContractInCache);
  const travelDetails = useSelector(travelDataSelector);
  const hasAgreed = useSelector((state) => state.travelContract.hasAgreed);
  const travelers = useSelector((state) => state.travelContract.contractData.travelers);

  const formProps = useForm({
    defaultValues: {
      ...travelDetails,
      start: travelDetails.range.start,
      end: travelDetails.range.end,
      hasAgreed,
    },
    validationSchema: contractCalculatorSchema,
  });

  const earliestAge = useMemo(() => {
    const existingTravelers = travelers || [];
    const birthdays = existingTravelers.map((traveler) => traveler.birthday);
    const validBirthdays = birthdays.filter((date) => !!date);
    const earliestBirthday = getEarliestDateFromArray(validBirthdays) || null;
    return earliestBirthday ? getDiffInYearsFromNow(earliestBirthday) : 0;
  }, [travelers]);

  useEffect(() => {
    setSubmitCount(formProps.formState.submitCount);
  }, [formProps.formState.submitCount]);

  useScrollToFormErrorElement(formProps.errors, submitCount, previousCount);

  const handleAgreementClick = () => {
    dispatch(setHasAgreed(!hasAgreed));
    const { errors, triggerValidation } = formProps;
    if (errors.hasAgreed) {
      triggerValidation({ name: 'hasAgreed' });
    }
  };

  const next = useCallback(() => {
    formProps.triggerValidation();
    dispatch(clearCalculationBackendErrors());
    if (travelDetails.selectedPlan && formProps.getValues().hasAgreed) {
      if (isAuthenticated) {
        dispatch(saveDraftContract());
      }
      dispatch(saveTravelContractInCache()).then(
        () => goNext(),
        () => { },
      );
    }
  }, [dispatch, formProps, goNext, isAuthenticated, travelDetails.selectedPlan]);

  const handleNext = useCallback(() => {
    formProps.handleSubmit(next)();
  }, [formProps, next]);

  useEffect(() => {
    if (!isEmpty(formProps.errors) || !hasAgreed) {
      blockNextSteps();
    }
  }, [formProps.errors, hasAgreed, blockNextSteps]);

  const errorsLength = Object.keys(formProps.errors).length;

  const { region, prices, selectedPlan, selectedDestinations } = travelDetails;

  const hasOnlyLiteDisallowedCountry = useMemo(
    () => getOnlyLiteDisallowedCountry(selectedDestinations), [selectedDestinations],
  );

  useEffect(() => {
    dispatch(loadAvailableDestinations());
  }, [dispatch]);

  const dispatchSelectedPlan = useCallback((plan) => dispatch(selectPlan(plan, actionName)), [
    dispatch,
    actionName,
  ]);

  const dispatchSelectCoverages = useCallback(
    (coverages) => dispatch(selectCoverages(coverages, actionName)),
    [dispatch, actionName],
  );

  const dispatchSelectedPackage = useCallback(
    (newPackage) => dispatch(selectPackage(newPackage, actionName)),
    [dispatch, actionName],
  );

  const dispatchSelectedLimit = useCallback(
    (newLimit) => dispatch(selectLimit(newLimit, actionName)),
    [dispatch, actionName],
  );

  const dispatchAge = useCallback((newAge) => dispatch(setAge(newAge, actionName)), [
    dispatch,
    actionName,
  ]);

  const dispatchRegion = useCallback((newRegion) => dispatch(setRegion(newRegion, actionName)), [
    dispatch,
    actionName,
  ]);

  const dispatchDestinations = useCallback(
    (newDestinations) => dispatch(selectDestinations(newDestinations, actionName)),
    [dispatch, actionName],
  );

  const dispatchDays = useCallback((newDays) => dispatch(setDays(newDays, actionName)), [
    dispatch,
    actionName,
  ]);

  const dispatchRange = useCallback((newRange) => dispatch(setRange(newRange, actionName)), [
    dispatch,
    actionName,
  ]);

  const plansWithAllowedLimitRegionAndAge = useMemo(
    () => getPlansWithAllowedLimitRegionAndAge(region, earliestAge, hasOnlyLiteDisallowedCountry),
    [earliestAge, region, hasOnlyLiteDisallowedCountry],
  );

  useEffect(() => {
    setStepNavigationBox(() => (
      <>
        {isDeviceLgSize && (
          <Grid
            container
            alignItems="center"
            className={classes.stepNavigationAmount}
            onClick={handleOpenMobileSummary}
          >
            <StepNavigationAmount amount={totalPrice} iconDown={false} isMobile={isDeviceLgSize} />
          </Grid>
        )}
        <StepsNavigation
          back={goPrevious}
          next={handleNext}
          isNextDisabled={isFetchingPrice || !!errorsLength}
          width={isDeviceLgSize ? MAIN_BUTTON_WIDTH_KEYS.EXTRA_LARGE : ''}
        />
      </>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classes.stepNavigationAmount, errorsLength, isDeviceLgSize, isFetchingPrice, totalPrice]);

  const discountedPrice = useMemo(
    () => getDiscountedPrice(
        travelDetails?.policyPrices, selectedPlan,
    ), [selectedPlan, travelDetails.policyPrices],
  );

  return (
    <div>
      {isSavingCache && loader}
      <TravelCalculator
        age={earliestAge}
        setAge={dispatchAge}
        showAgeInput={false}
        travelDetails={travelDetails}
        prices={prices}
        setSelectedPlan={dispatchSelectedPlan}
        onCoverageChange={dispatchSelectCoverages}
        setSelectedLimit={dispatchSelectedLimit}
        setRegion={dispatchRegion}
        setDestinations={dispatchDestinations}
        setDays={dispatchDays}
        setRange={dispatchRange}
        setSelectedPackage={dispatchSelectedPackage}
        formProps={formProps}
        hasNarrowView
        fullWidthElement
        amount={discountedPrice}
        plansWithAllowedLimitRegionAndAge={plansWithAllowedLimitRegionAndAge}
        showNavigationButtons={false}
        actionName={ACTION_NAME.CONTRACT}
      />
      {isDeviceLgSize && (
        <Box sx={{ mt: '2rem' }}>
          <TitleTypography component="div" className={classes.sectionTitle}>
            {t('promoCode.promoCodes')}
          </TitleTypography>
          <PromoCodes data={params.data} />
        </Box>
      )}
      <InfoBlock
        formProps={formProps}
        handleAgreementClick={handleAgreementClick}
        hasAgreed={hasAgreed}
        showInfoBlock
        infoBlockText="travelContract.termsOfService"
        agreementText="travelContract.attentionMsg"
      />
    </div>
  );
};

CalculationStep.propTypes = {
  goPrevious: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
  blockNextSteps: PropTypes.func.isRequired,
  params: PropTypes.shape({
    data: PropTypes.shape({
      signer: PropTypes.shape({}),
    }).isRequired,
    isFetchingPrice: PropTypes.bool,
    totalPrice: PropTypes.number,
    loader: PropTypes.element.isRequired,
    actionName: PropTypes.string.isRequired,
    isDeviceLgSize: PropTypes.bool.isRequired,
    handleOpenMobileSummary: PropTypes.func.isRequired,
    setStepNavigationBox: PropTypes.func.isRequired,
  }),
};

CalculationStep.defaultProps = {
  params: {
    isFetchingPrice: false,
    totalPrice: null,
  },
};

export default CalculationStep;
